import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { translatePageText } from '@/i18n/pageText';

type TextState = { original: string; translated: string };
type AttributeState = Record<string, { original: string; translated: string }>;

const textStates = new WeakMap<Text, TextState>();
const attributeStates = new WeakMap<Element, AttributeState>();
const translatedAttributes = ['placeholder', 'title', 'aria-label'];

function withWhitespace(source: string, translated: string) {
  const leading = source.match(/^\s*/)?.[0] ?? '';
  const trailing = source.match(/\s*$/)?.[0] ?? '';
  return `${leading}${translated}${trailing}`;
}

function translateNode(node: Node, english: boolean) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node as Text;
    const current = text.data;
    const existing = textStates.get(text);

    if (!english) {
      if (existing && current === existing.translated) text.data = existing.original;
      textStates.delete(text);
      return;
    }

    const original = existing && current === existing.translated ? existing.original : current;
    const trimmed = original.trim();
    if (!trimmed) return;
    const result = translatePageText(trimmed);
    if (result !== trimmed) {
      const translated = withWhitespace(original, result);
      textStates.set(text, { original, translated });
      if (text.data !== translated) text.data = translated;
    }
    return;
  }

  if (!(node instanceof Element)) return;
  const saved = attributeStates.get(node) ?? {};
  for (const attribute of translatedAttributes) {
    const current = node.getAttribute(attribute);
    if (current == null) continue;
    const existing = saved[attribute];
    if (!english) {
      if (existing && current === existing.translated) node.setAttribute(attribute, existing.original);
      delete saved[attribute];
      continue;
    }
    const original = existing && current === existing.translated ? existing.original : current;
    const translated = translatePageText(original);
    if (translated !== original) {
      saved[attribute] = { original, translated };
      if (current !== translated) node.setAttribute(attribute, translated);
    }
  }
  attributeStates.set(node, saved);
  node.childNodes.forEach((child) => translateNode(child, english));
}

export default function PageTranslator({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const english = !i18n.resolvedLanguage?.startsWith('ja');

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    translateNode(root, english);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') translateNode(mutation.target, english);
        mutation.addedNodes.forEach((node) => translateNode(node, english));
        if (mutation.type === 'attributes') translateNode(mutation.target, english);
      }
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: translatedAttributes });
    return () => {
      observer.disconnect();
      translateNode(root, false);
    };
  }, [english]);

  return <div ref={rootRef}>{children}</div>;
}
